import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useSignup } from '@/lib/mutations';
import { toFieldErrors, toMessage } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useToast } from '@/components/ui/Toast';
import { NOUN, pluralize } from '@/lib/plural';

const PASSWORD_MIN = 10;

const schema = z
  .object({
    email: z.string().min(1, 'Введите адрес почты.').email('Некорректный адрес почты.'),
    phone: z
      .string()
      .trim()
      .regex(/^\+?[0-9 ()-]{10,20}$/, 'Введите номер в международном формате.')
      .optional()
      .or(z.literal('')),
    password: z
      .string()
      .min(PASSWORD_MIN, `Минимум ${pluralize(PASSWORD_MIN, NOUN.character)}.`)
      .regex(/[a-z]/, 'Добавьте хотя бы одну строчную букву.')
      .regex(/[A-Z]/, 'Добавьте хотя бы одну заглавную букву.')
      .regex(/[0-9]/, 'Добавьте хотя бы одну цифру.'),
    confirmPassword: z.string().min(1, 'Повторите пароль.'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Пароли не совпадают.',
  });

type FormValues = z.infer<typeof schema>;

export default function SignupPage() {
  const navigate = useNavigate();
  const signIn = useAuthStore((state) => state.signIn);
  const signup = useSignup();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', phone: '', password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const result = await signup.mutateAsync({
        email: values.email,
        password: values.password,
        ...(values.phone ? { phone: values.phone } : {}),
      });

      // Some server builds register without issuing a token; in that case send
      // the trader to sign in rather than leaving them on a dead form.
      if (result.tokens.accessToken) {
        signIn(result.tokens.accessToken, result.tokens.expiresIn, result.trader);
        navigate('/verification', { replace: true });
        return;
      }

      toast.success('Аккаунт создан', 'Войдите, чтобы начать верификацию.');
      navigate('/login', { replace: true });
    } catch (error) {
      const fieldErrors = toFieldErrors(error);
      let matched = false;
      for (const [field, message] of Object.entries(fieldErrors)) {
        if (field === 'email' || field === 'password' || field === 'phone') {
          setError(field, { message });
          matched = true;
        }
      }
      if (!matched) setServerError(toMessage(error, 'Не удалось создать аккаунт.'));
    }
  });

  return (
    <div>
      <p className="eyebrow">Регистрация</p>
      <h1 className="mt-2 text-xl font-semibold leading-tight tracking-tight text-fg">
        Создание аккаунта трейдера
      </h1>
      <p className="mt-2 text-sm text-fg-secondary">
        Прежде чем на вас пойдёт трафик, понадобятся паспорт, селфи и хотя бы одна банковская карта,
        оформленная на ваше имя.
      </p>

      {serverError ? <InlineError message={serverError} className="mt-6" /> : null}

      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
        <TextField
          label="Почта"
          type="email"
          inputMode="email"
          enterKeyHint="next"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          autoFocus
          spellCheck={false}
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <TextField
          label="Телефон"
          type="tel"
          inputMode="tel"
          enterKeyHint="next"
          autoComplete="tel"
          monospace
          placeholder="+7 900 000 00 00"
          hint="Необязательно. Нужен только для восстановления доступа."
          error={errors.phone?.message}
          {...register('phone')}
        />

        <TextField
          label="Пароль"
          type="password"
          enterKeyHint="next"
          autoComplete="new-password"
          hint={`Минимум ${pluralize(PASSWORD_MIN, NOUN.character)}: заглавные и строчные буквы плюс цифра.`}
          error={errors.password?.message}
          {...register('password')}
        />

        <TextField
          label="Повторите пароль"
          type="password"
          enterKeyHint="go"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          className="mt-6"
        >
          {isSubmitting ? 'Создаю аккаунт…' : 'Создать аккаунт'}
        </Button>
      </form>

      <p className="mt-8 border-t border-line pt-5 text-sm text-fg-secondary">
        Уже зарегистрированы?{' '}
        <Link
          to="/login"
          className="focus-ring tap-target inline-flex rounded-sm font-medium text-fg-accent underline underline-offset-2"
        >
          Войти
        </Link>
      </p>
    </div>
  );
}
